import React, { useEffect, useState } from "react";
import { Inbox, Clock, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "../api";

const STATUS_LABELS = {
  sent: "Leveret", retrying: "Prøver igen", permanently_failed: "Fejlet",
  pending: "Afventer", ukendt: "Ukendt",
};
const OMR_LABELS = {
  "boern-unge-familier": "Børn, unge og familier",
  "voksne-social-stabilisering": "Voksne / social stabilisering",
  "beskaeftigelse-fastholdelse": "Beskæftigelse / fastholdelse",
  "loesladelse-resocialisering": "Løsladelse / resocialisering",
  andet: "Andet", ukendt: "Ikke angivet", "": "Ikke angivet",
};

const Stat = ({ icon: Icon, label, value, tint }) => (
  <div className="rounded-2xl bg-white border border-black/5 p-5" data-testid={`admin-stat-${label}`}>
    <span className={`inline-flex items-center justify-center h-10 w-10 rounded-full ${tint} mb-3`}>
      <Icon className="h-5 w-5" strokeWidth={1.75} />
    </span>
    <p className="text-3xl font-display text-[#1A2E24]">{value}</p>
    <p className="text-sm text-[#5C7567] mt-0.5">{label}</p>
  </div>
);

const Bars = ({ title, data, labels, testid }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-6" data-testid={testid}>
      <h3 className="font-display text-lg text-[#1A2E24] mb-4">{title}</h3>
      <ul className="space-y-3">
        {entries.map(([k, v]) => (
          <li key={k}>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#1A2E24]">{(labels && labels[k]) || k}</span>
              <span className="text-[#5C7567]">{v}</span>
            </div>
            <div className="h-2 rounded-full bg-[#EAE5D9] overflow-hidden">
              <div className="h-full bg-[#2D5A3F]" style={{ width: `${(v / max) * 100}%` }} />
            </div>
          </li>
        ))}
        {entries.length === 0 && <li className="text-sm text-[#5C7567]">Ingen data.</li>}
      </ul>
    </div>
  );
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.dashboard().then(setData).catch(() => setError("Kunne ikke hente overblik."));
  }, []);

  if (error) return <p className="text-red-600" data-testid="admin-dashboard-error">{error}</p>;
  if (!data)
    return (
      <div className="flex items-center gap-2 text-[#5C7567]" data-testid="admin-dashboard-loading">
        <Loader2 className="h-5 w-5 animate-spin" /> Henter overblik…
      </div>
    );

  const ds = data.by_delivery_status || {};
  return (
    <div data-testid="admin-dashboard-page">
      <header className="mb-8">
        <h1 className="font-display text-3xl text-[#1A2E24]">Overblik</h1>
        <p className="text-sm text-[#5C7567] mt-1">Aggregerede tal — ingen personoplysninger vises her.</p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Inbox} label="Henvendelser i alt" value={data.total_messages} tint="bg-[#2D5A3F]/10 text-[#2D5A3F]" />
        <Stat icon={Clock} label="Seneste 7 dage" value={data.recent_7_days} tint="bg-[#C47C5D]/15 text-[#C47C5D]" />
        <Stat icon={CheckCircle2} label="Leveret" value={ds.sent || 0} tint="bg-green-100 text-green-700" />
        <Stat icon={AlertTriangle} label="Fejlet / afventer" value={(ds.permanently_failed || 0) + (ds.retrying || 0) + (ds.pending || 0) + (ds.ukendt || 0)} tint="bg-amber-100 text-amber-700" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Bars title="Leveringsstatus" data={ds} labels={STATUS_LABELS} testid="admin-dashboard-delivery" />
        <Bars title="Indsatsområde" data={data.by_indsatsomraade} labels={OMR_LABELS} testid="admin-dashboard-omraade" />
      </div>
    </div>
  );
};

export default Dashboard;
